import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ModalMode } from '@solcre-org/core-ui';
import { BookingModel } from '../../../shared/models/booking.model';
import { PortCallModel } from '../../../shared/models/port-call.model';
import { PortModel } from '../../../shared/models/port.model';
import { VoyageModel } from '../../../shared/models/voyage.model';
import { portNameTemplate } from '../../../shared/templates/portNameTemplate';
import { voyageNameTemplate } from '../../../shared/templates/voyageNameTemplate';

export interface SelectOption {
  value: any;
  label: string;
}

export interface VoyagePortCallData {
  voyageUri: string;
  portCalls: PortCallModel[];
}

export interface LoadingVoyageState {
  id: string;
}

type TransshipmentFormValue = Pick<
  BookingModel,
  'voyage' | 'secondVoyage' | 'transshipmentPort' | 'requiresTransshipmentToggle'
>;

const hasValue = (value: unknown): boolean => {
  return value !== null && value !== undefined && value !== '';
};

export const buildPortSelectOptions = (
  ports: readonly PortModel[]
): SelectOption[] => {
  return ports.map((port: PortModel) => ({
    value: port.id,
    label: portNameTemplate(port),
  }));
};

export const buildSecondVoyageSelectOptions = (
  voyages: readonly VoyageModel[],
  primaryVoyage?: string | null,
  labelBuilder: (voyage: VoyageModel) => string = voyageNameTemplate
): SelectOption[] => {
  const options: SelectOption[] = [
    { value: 'TBN', label: 'TBN' }
  ];

  const voyageOptions = voyages
    .filter((voyage: VoyageModel) => voyage.id !== primaryVoyage)
    .map((voyage: VoyageModel) => ({
      value: voyage.id,
      label: labelBuilder(voyage),
    }));

  return [...options, ...voyageOptions];
};

export const sortPortCallsBySequence = (
  portCalls: PortCallModel[]
): PortCallModel[] => {
  return [...portCalls].sort(
    (left, right) => (left.sequenceOrder ?? 0) - (right.sequenceOrder ?? 0)
  );
};

export const collectPortUris = (portCalls: PortCallModel[]): Set<string> => {
  return new Set(
    portCalls
      .map((portCall: PortCallModel) => portCall.port)
      .filter((port): port is string => !!port)
  );
};

export const filterVoyagePortOptions = (
  allPorts: SelectOption[],
  portCalls: PortCallModel[]
): SelectOption[] => {
  const voyagePortUris = collectPortUris(portCalls);
  return allPorts.filter((port: SelectOption) => voyagePortUris.has(port.value));
};

export const filterLegPortOptions = (
  allPorts: SelectOption[],
  portCalls: PortCallModel[],
  hubPort: string | null | undefined,
  beforeHub: boolean
): SelectOption[] => {
  const voyagePortOptions = filterVoyagePortOptions(allPorts, portCalls);

  if (!hubPort) {
    return voyagePortOptions;
  }

  const hubPortCall = portCalls.find(
    (portCall: PortCallModel) => portCall.port === hubPort
  );

  if (!hubPortCall) {
    return voyagePortOptions;
  }

  const hubSequence = hubPortCall.sequenceOrder ?? 0;
  const allowedPortUris = new Set(
    portCalls
      .filter((portCall: PortCallModel) =>
        beforeHub
          ? (portCall.sequenceOrder ?? 0) < hubSequence
          : (portCall.sequenceOrder ?? 0) > hubSequence
      )
      .map((portCall: PortCallModel) => portCall.port)
      .filter((port): port is string => !!port)
  );

  const filteredOptions = allPorts.filter((port: SelectOption) =>
    allowedPortUris.has(port.value)
  );

  return filteredOptions.length > 0 ? filteredOptions : voyagePortOptions;
};

export const filterSharedTransshipmentPortOptions = (
  allPorts: SelectOption[],
  primaryPortCalls: PortCallModel[],
  secondaryPortCalls: PortCallModel[]
): SelectOption[] => {
  const primaryPortUris = collectPortUris(primaryPortCalls);
  const sharedPortUris = new Set(
    secondaryPortCalls
      .map((portCall: PortCallModel) => portCall.port)
      .filter(
        (port): port is string => !!port && primaryPortUris.has(port)
      )
  );

  return allPorts.filter((port: SelectOption) =>
    sharedPortUris.has(port.value)
  );
};

export const resolveSharedConnectionPort = (
  primaryPortCalls: readonly PortCallModel[],
  secondaryPortCalls: readonly PortCallModel[],
  originPort: string | null | undefined,
  destinationPort: string | null | undefined,
  sameVessel = false
): string | null => {
  if (!originPort || !destinationPort) {
    return null;
  }

  const primarySequences = new Map<string, number[]>();
  const secondarySequences = new Map<string, number[]>();
  const addSequence = (
    index: Map<string, number[]>,
    portCall: PortCallModel
  ): void => {
    if (!portCall.port) {
      return;
    }

    const sequences = index.get(portCall.port) ?? [];
    sequences.push(portCall.sequenceOrder ?? Number.MAX_SAFE_INTEGER);
    index.set(portCall.port, sequences);
  };

  primaryPortCalls.forEach(portCall => addSequence(primarySequences, portCall));
  secondaryPortCalls.forEach(portCall =>
    addSequence(secondarySequences, portCall)
  );

  const hasAscendingPair = (
    origins: readonly number[],
    destinations: readonly number[]
  ): boolean =>
    origins.some(origin => destinations.some(destination => origin < destination));

  const candidates = [...primarySequences.keys()].filter(port => {
    const secondaryPortSequences = secondarySequences.get(port);

    return (
      secondaryPortSequences !== undefined &&
      hasAscendingPair(
        primarySequences.get(originPort) ?? [],
        primarySequences.get(port) ?? []
      ) &&
      hasAscendingPair(
        secondaryPortSequences,
        secondarySequences.get(destinationPort) ?? []
      )
    );
  });

  if (sameVessel) {
    const orderedPrimaryPortCalls = sortPortCallsBySequence([...primaryPortCalls]);
    const orderedSecondaryPortCalls = sortPortCallsBySequence([...secondaryPortCalls]);
    const lastPrimaryPort = orderedPrimaryPortCalls.at(-1)?.port;
    const firstSecondaryPort = orderedSecondaryPortCalls[0]?.port;

    if (
      lastPrimaryPort &&
      lastPrimaryPort === firstSecondaryPort &&
      candidates.includes(lastPrimaryPort)
    ) {
      return lastPrimaryPort;
    }
  }

  return candidates.length === 1 ? candidates[0] : null;
};

export const resolveRequiresTransshipmentToggle = (
  booking?: Partial<TransshipmentFormValue> | null
): boolean => {
  if (
    booking?.requiresTransshipmentToggle !== undefined &&
    booking?.requiresTransshipmentToggle !== null
  ) {
    return !!booking.requiresTransshipmentToggle;
  }

  return !!(
    hasValue(booking?.secondVoyage) ||
    hasValue(booking?.transshipmentPort)
  );
};

export const createPairedFieldValidator = (
  otherValue: unknown,
  errorKey = 'pairedFieldRequired'
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (hasValue(otherValue) && !hasValue(control.value)) {
      return { [errorKey]: true };
    }

    return null;
  };
};

export const createDifferentVoyageValidator = (
  primaryVoyage?: string | null
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (
      hasValue(control.value) &&
      hasValue(primaryVoyage) &&
      control.value === primaryVoyage
    ) {
      return { sameVoyage: true };
    }

    return null;
  };
};

export const normalizeBookingTransshipmentPayload = (
  booking: BookingModel,
  mode: ModalMode
): BookingModel => {
  const requiresTransshipment = resolveRequiresTransshipmentToggle(booking);

  if (!requiresTransshipment) {
    booking.secondVoyage = mode === ModalMode.CREATE ? undefined : null;
    booking.transshipmentPort = mode === ModalMode.CREATE ? undefined : null;
    return booking;
  }

  if (!hasValue(booking.secondVoyage)) {
    booking.secondVoyage = undefined;
  }

  if (!hasValue(booking.transshipmentPort)) {
    booking.transshipmentPort = undefined;
  }

  return booking;
};

export const extractBookingApiErrorMessage = (
  error: any,
  fallbackMessage: string
): string => {
  const firstViolation = error?.error?.violations?.[0];
  if (typeof firstViolation?.message === 'string' && firstViolation.message) {
    return firstViolation.message;
  }

  if (typeof error?.error?.detail === 'string' && error.error.detail) {
    return error.error.detail;
  }

  return fallbackMessage;
};
